import React, { createContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Platform, NativeModules } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
// Import from local translations file (copy of shared/translations.ts)
import { translations, Language } from '../translations';
import config from '../config';
// Note: Mobile app doesn't have an API to fetch language settings, so we'll enable all by default.

interface LocalizationContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: string, params?: Record<string, string | number>) => string;
    availableLanguages: Language[];
}

export const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined);

export const LocalizationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Detect device language synchronously for initial state (no flash of wrong language)
    const getDeviceLanguageSync = (): Language => {
        try {
            let deviceLang: string = 'en';
            
            if (Platform.OS === 'web') {
                // Web: Use browser language
                deviceLang = (typeof navigator !== 'undefined' && navigator.language) 
                    ? navigator.language.split('-')[0].toLowerCase()
                    : 'en';
            } else {
                // Native: Use device locale
                const locale = NativeModules.I18nManager?.localeIdentifier || 
                              NativeModules.SettingsManager?.settings?.AppleLocale ||
                              NativeModules.SettingsManager?.settings?.AppleLanguages?.[0] ||
                              'en';
                deviceLang = locale.split('-')[0].split('_')[0].toLowerCase();
            }
            
            // Map to supported languages
            if (['en', 'de', 'tr'].includes(deviceLang)) {
                return deviceLang as Language;
            }
            return 'en';
        } catch (error) {
            console.warn('Failed to detect device language:', error);
            return 'en';
        }
    };

    // Initialize with device language immediately (prevents flash of wrong language)
    const [language, setLanguageState] = useState<Language>(getDeviceLanguageSync());
    const [availableLanguages, setAvailableLanguages] = useState<Language[]>(['en', 'de', 'tr']); // Start with all languages

    // Detect device language (async version for updates)
    const getDeviceLanguage = (): Language => {
        return getDeviceLanguageSync();
    };

    useEffect(() => {
        // Load language from storage or detect from device
        const initializeLanguage = async () => {
            const storedLang = await AsyncStorage.getItem('language');
            
            if (storedLang && ['en', 'de', 'tr'].includes(storedLang)) {
                // User has previously selected a language
                setLanguageState(storedLang as Language);
            } else {
                // First time: Use device language
                const deviceLang = getDeviceLanguage();
                setLanguageState(deviceLang);
                await AsyncStorage.setItem('language', deviceLang);
                console.log('🌍 Auto-detected device language:', deviceLang);
            }
        };
        
        initializeLanguage();

        // Fetch language settings in background (non-blocking, with timeout)
        // This is not critical for app startup, so we use a short timeout
        const getInitialLang = async () => {
            try {
                // Get current user role
                const userString = await AsyncStorage.getItem('user');
                const userData = userString ? JSON.parse(userString) : null;
                const userRole = userData?.user?.role;
                
                // Fetch enabled languages from backend with timeout (3 seconds)
                // Don't block app startup if backend is slow
                const fetchPromise = fetch(`${config.API_BASE_URL}/settings/languages`);
                const timeoutPromise = new Promise<Response>((_, reject) => 
                    setTimeout(() => reject(new Error('Language settings timeout')), 3000)
                );
                
                let enabledLangs: Language[] = ['en', 'de', 'tr']; // Fallback
                
                try {
                    const response = await Promise.race([fetchPromise, timeoutPromise]);
                    if (response.ok) {
                        const data = await response.json();
                        enabledLangs = data.enabledLanguages || ['en', 'de', 'tr'];
                        console.log('✅ Enabled languages from backend:', enabledLangs);
                    } else {
                        console.warn('⚠️ Failed to fetch language settings, using fallback');
                    }
                } catch (fetchError) {
                    // Timeout or network error - use fallback
                    console.warn('⚠️ Language settings fetch timeout/error, using fallback');
                }
                
                // Super Admin can use ALL languages, regardless of settings
                if (userRole === 'super-admin') {
                    setAvailableLanguages(['en', 'de', 'tr']); // ALL languages
                    console.log('👑 Super Admin: All languages available');
                } else {
                    setAvailableLanguages(enabledLangs); // Only enabled languages
                    
                    // If stored language is disabled AND user is NOT super-admin, switch to English
                    const currentStoredLang = await AsyncStorage.getItem('language');
                    if (currentStoredLang && !enabledLangs.includes(currentStoredLang as Language)) {
                        console.warn(`⚠️ Language ${currentStoredLang} is disabled, switching to English`);
                        setLanguageState('en');
                        await AsyncStorage.setItem('language', 'en');
                    }
                }
            } catch (e) {
                console.warn('⚠️ Error fetching language settings (non-critical):', e);
                // Keep fallback - already set in initial state
            }
        };
        getInitialLang();
    }, []);

    const setLanguage = async (lang: Language) => {
        setLanguageState(lang);
        await AsyncStorage.setItem('language', lang);
    };
    
    const t = useCallback((key: string, params?: Record<string, string | number>): string => {
        let translation = translations[language]?.[key] || translations['en']?.[key] || key;
        if (params) {
            Object.keys(params).forEach(paramKey => {
                translation = translation.replace(`{${paramKey}}`, String(params[paramKey]));
            });
        }
        return translation;
    }, [language]);

    // Always render immediately (non-blocking)
    return (
        <LocalizationContext.Provider value={{ language, setLanguage, t, availableLanguages }}>
            {children}
        </LocalizationContext.Provider>
    );
};

export const useLocalization = () => {
    const context = React.useContext(LocalizationContext);
    if (!context) {
        throw new Error('useLocalization must be used within LocalizationProvider');
    }
    return context;
};